import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Loader2 } from "lucide-react";

const PLANS = ["starter", "pro", "enterprise"];

export default function AdminCreateEmployerDialog({ open, onOpenChange, onCreated }) {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", password: "", plan: "starter" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.company_name || !form.contact_name || !form.email || !form.password) {
      toast.error(lang === "fr" ? "Tous les champs sont requis" : "All fields are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error(lang === "fr" ? "Mot de passe ≥ 6 caractères" : "Password ≥ 6 characters");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/employers`, form, { headers: authHeader });
      toast.success(lang === "fr" ? "Employeur créé ✓" : "Employer created ✓");
      onCreated && onCreated(r.data);
      setForm({ company_name: "", contact_name: "", email: "", password: "", plan: "starter" });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-create-employer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {lang === "fr" ? "Créer un employeur" : "Create employer"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>{lang === "fr" ? "Nom de l'entreprise" : "Company name"}</Label>
            <Input data-testid="admin-emp-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div>
            <Label>{lang === "fr" ? "Contact" : "Contact name"}</Label>
            <Input data-testid="admin-emp-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="admin-emp-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>{lang === "fr" ? "Mot de passe" : "Password"}</Label>
            <Input data-testid="admin-emp-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div>
            <Label>Plan</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger data-testid="admin-emp-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLANS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
              {lang === "fr" ? "Annuler" : "Cancel"}
            </Button>
            <Button type="submit" data-testid="admin-emp-submit" disabled={saving} className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (lang === "fr" ? "Créer" : "Create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
